import React, {useState, useEffect, ChangeEvent, forwardRef} from 'react';
import styled from 'styled-components';
import {TextInput} from '@volkswagen-onehub/components-core';
import InputWrapperWithLabel from './StyledInputWrapperWithLabel';
import Icon from './Icon';
import {Variants, motion} from 'framer-motion';

const variants: Variants = {
  hidden: {opacity: 0},
  show: {
    opacity: 1,
    transition: {
      delay: 1.25,
    },
  },
};

const StyledFooterField = styled(motion.div)<{
  isMobile: boolean;
  marginTop?: string;
  isInputFocused: boolean;
}>`
  position: relative;
  width: 100%;
  max-width: ${(props) => (props.isMobile ? '350px' : '600px')};
  margin-top: ${(props) => props.marginTop || 'auto'};
  align-items: center;
  border: ${(props) =>
    props.isInputFocused
      ? '2px solid rgba(255, 255, 255, 1)'
      : '2px solid rgba(255, 255, 255, 0.5)'};
  border-radius: 30px;
  padding: 0 24px;
  display: flex;
  align-items: center;
  transition: border 0.2s;
  &:hover {
    border: 2px solid rgba(255, 255, 255, 1);
  }
`;

const StyledTextField = styled(TextInput)<{isMobile: boolean}>`
  width: 100%;
  height: 100%;
  outline: none;
  background-color: transparent;
  color: var(--1-white-000, #fff);
  &::placeholder {
    opacity: 0.5;
    color: var(--1-white-000, #fff);
  }
`;

const StyledInputWrapperWithLabel = styled(InputWrapperWithLabel)`
  flex-grow: 1;
  flex: 1;
  input {
    border-bottom: none;
    min-width: unset !important;

    &:focus:not(:read-only) {
      box-shadow: none;
      border-bottom: none;
      color: var(--1-white-000, #fff);
    }

    &:hover:not(:read-only) {
      box-shadow: none;
    }
  }

  button {
    margin-right: -12px;
    margin-top: 4px;
    opacity: 0.5;
    transition: opacity 0.2s;

    &:hover:not(:disabled) {
      color: #fff;
      opacity: 1;
    }
  }

  button:hover::before {
    background-color: transparent;
  }

  button:hover {
    opacity: 1;
  }

  button:active {
    outline: none;
    border: none;
    background-color: transparent;
  }

  button:focus {
    outline: none;
    border: none;
    background-color: transparent;
  }
`;
const StyledSendIcon = styled(Icon)`
  opacity: 0.5;
  position: absolute;
  right: 24px;
`;

type ChatTextFieldProps = {
  value: string;
  isMobile: boolean;
  marginTop?: string;
  placeholder?: string;
  withAnimation?: boolean;
  onChange?: (value: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: () => void;
};

const ChatTextField = forwardRef<HTMLInputElement, ChatTextFieldProps>(
  (
    {
      value,
      isMobile,
      withAnimation = false,
      marginTop,
      placeholder,
      onChange,
      onKeyDown,
      onFocus,
    },
    inputRef,
  ) => {
    const [inputValue, setInputValue] = useState('');
    const [isInputFocused, setInputFocused] = useState<boolean>(false);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      setInputValue(newValue);
      onChange?.(event.target.value);
    };

    useEffect(() => {
      setInputValue(value);
    }, [value]);

    const handleInputFocus = () => {
      setInputFocused(true);
      onFocus?.();
    };

    const handleInputBlur = () => {
      setInputFocused(false);
    };

    return (
      <StyledFooterField
        variants={withAnimation ? variants : {}}
        initial="hidden"
        animate="show"
        isMobile={isMobile}
        marginTop={marginTop}
        isInputFocused={isInputFocused}
      >
        <StyledInputWrapperWithLabel>
          <StyledTextField
            ref={inputRef}
            appearance={'default'}
            placeholder={placeholder || 'Ask me anything'}
            isMobile={isMobile}
            value={inputValue}
            onChange={(event) =>
              handleChange(event as ChangeEvent<HTMLInputElement>)
            }
            hasClearButton={!!inputValue}
            isClearButtonVisible
            onClear={() => {
              setInputValue('');
              onChange?.('');
            }}
            clearIconLabel="Clear search"
            onKeyDown={onKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
          />
        </StyledInputWrapperWithLabel>
        {!inputValue && <StyledSendIcon icon="arrow-right-alt" size={24} />}
      </StyledFooterField>
    );
  },
);

ChatTextField.displayName = 'ChatTextField';

export default ChatTextField;
